import { Loader2, Monitor, Smartphone, Upload, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { showError, showLoading, showSuccess } from "../../../utils/toastUtils";
import uploadFileToCloudinary from "../../../utils/uploadFileToCloudinary";
const SCROLL_TARGETS = [
    "hero",
    "categories",
    "packages",
    "testimonials",
    "counter",
    "choose-us",
    "work",
    "gallery",
    "insight",
    "faqs",
    "newsletter",
];
const EditBannerModal = ({ isOpen, onClose, banner, onSave }) => {
    const imageInputRef = useRef(null);
    const videoInputRef = useRef(null);

    const isEdit = !!banner;

    const [formData, setFormData] = useState({
        title: "",
        device: "desktop",
        imagePreview: "",
        imageFile: null,
        videoUrl: "",
        videoFile: null,
        link: "",
        sequence: 0,
        active: true,
    });

    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);

    // ================= INIT =================
    useEffect(() => {
        if (banner) {
            setFormData({
                title: banner.title || "",
                device: banner.device || "desktop",
                imagePreview: banner.imageUrl || "",
                imageFile: null,
                videoUrl: banner.videoUrl || "",
                videoFile: null,
                link: banner.link || "",
                sequence: banner.sequence ?? 0,
                active: banner.active ?? true,
            });
        } else {
            setFormData({
                title: "",
                device: "desktop",
                imagePreview: "",
                imageFile: null,
                videoUrl: "",
                videoFile: null,
                link: "",
                sequence: 0,
                active: true,
            });
        }
    }, [banner, isOpen]);

    // ================= FILE HANDLERS =================
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            showError("Only image files allowed");
            return;
        }

        setFormData((p) => ({
            ...p,
            imageFile: file,
            imagePreview: URL.createObjectURL(file),
        }));
    };

    const handleVideoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith("video/")) {
            showError("Only video files allowed");
            return;
        }

        setFormData((p) => ({
            ...p,
            videoFile: file,
            videoUrl: URL.createObjectURL(file),
        }));
    };

    // ================= SUBMIT =================
    const handleSubmit = async () => {
        if (!formData.title.trim()) return showError("Title required");

        let imageUrl = formData?.imagePreview;
        let videoUrl = formData?.videoUrl;

        try {
            setUploading(true);

            // IMAGE UPLOAD
            if (formData.imageFile) {
                const loadingId = showLoading("Uploading image...");
                const { uploadPromise } = uploadFileToCloudinary(
                    formData.imageFile,
                    setProgress
                );

                const result = await uploadPromise;
                if (loadingId) toast.dismiss(loadingId);

                if (!result) throw new Error("Image upload failed");

                imageUrl = result.url;
                showSuccess("Image uploaded");
            }

            // VIDEO UPLOAD
            if (formData.videoFile) {
                const loadingId = showLoading("Uploading video...");
                const { uploadPromise } = uploadFileToCloudinary(
                    formData.videoFile,
                    setProgress
                );

                const result = await uploadPromise;
                if (loadingId) toast.dismiss(loadingId);

                if (!result) throw new Error("Video upload failed");

                videoUrl = result.url;
                showSuccess("Video uploaded");
            }

            onSave({
                title: formData.title,
                device: formData.device,
                imageUrl,
                videoUrl,
                link: formData.link,
                sequence: Number(formData.sequence) || 0,
                active: formData.active,
            });

            onClose();
        } catch (err) {
            showError(err.message || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    if (!isOpen) return null;

    // ================= UI =================
    return (
        <div
            onClick={onClose}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white w-full max-w-2xl rounded-xl shadow-lg max-h-[90vh] overflow-y-auto"
            >
                {/* HEADER */}
                <div className="sticky top-0 bg-white z-10 flex justify-between items-center p-5 border-b">
                    <h2 className="text-lg font-semibold">
                        {isEdit ? "Edit Banner" : "Create Banner"}
                    </h2>
                    <button onClick={onClose}>
                        <X />
                    </button>
                </div>

                {/* BODY */}
                <div className="p-5 space-y-5">

                    {/* TITLE */}
                    <input
                        type="text"
                        placeholder="Banner title"
                        value={formData.title}
                        onChange={(e) =>
                            setFormData((p) => ({ ...p, title: e.target.value }))
                        }
                        className="w-full border rounded px-3 py-2"
                    />

                    {/* DEVICE */}
                    <div className="grid grid-cols-2 gap-3">
                        <label className="flex items-center gap-2 border p-3 rounded cursor-pointer">
                            <input
                                type="radio"
                                checked={formData.device === "desktop"}
                                onChange={() =>
                                    setFormData((p) => ({ ...p, device: "desktop" }))
                                }
                            />
                            <Monitor /> Desktop
                        </label>

                        <label className="flex items-center gap-2 border p-3 rounded cursor-pointer">
                            <input
                                type="radio"
                                checked={formData.device === "mobile"}
                                onChange={() =>
                                    setFormData((p) => ({ ...p, device: "mobile" }))
                                }
                            />
                            <Smartphone /> Mobile
                        </label>
                    </div>

                    {/* IMAGE */}
                    {!formData.imagePreview ? (
                        <label className="h-48 border-2 border-dashed flex flex-col items-center justify-center cursor-pointer">
                            <Upload />
                            Upload Image
                            <input
                                ref={imageInputRef}
                                hidden
                                type="file"
                                onChange={handleImageChange}
                            />
                        </label>
                    ) : (
                        <div className="relative">
                            <img
                                alt=""
                                src={formData.imagePreview}
                                className="w-full max-h-64 object-contain rounded"
                            />
                            <button
                                onClick={() =>
                                    setFormData((p) => ({
                                        ...p,
                                        imagePreview: "",
                                        imageFile: null,
                                    }))
                                }
                                className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full"
                            >
                                <X size={14} />
                            </button>
                        </div>
                    )}

                    {/* VIDEO */}
                    <div>
                        {!formData.videoUrl ? (
                            <label className="h-40 border-2 border-dashed flex items-center justify-center cursor-pointer">
                                Upload Video (optional)
                                <input
                                    ref={videoInputRef}
                                    hidden
                                    type="file"
                                    onChange={handleVideoChange}
                                />
                            </label>
                        ) : (
                            <div className="relative">
                                <video
                                    src={formData.videoUrl}
                                    controls
                                    className="w-full h-48 object-contain bg-black rounded"
                                />
                                <button
                                    onClick={() =>
                                        setFormData((p) => ({
                                            ...p,
                                            videoUrl: "",
                                            videoFile: null,
                                        }))
                                    }
                                    className="absolute top-2 right-2 bg-red-600 text-white p-2 rounded-full"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        )}
                    </div>

                    <select
                        value={formData.link}
                        onChange={(e) =>
                            setFormData((p) => ({
                                ...p,
                                link: e.target.value,
                            }))
                        }
                        className="w-full border rounded px-3 py-2"
                    >
                        <option value="">Select scroll target</option>

                        {SCROLL_TARGETS.map((target) => (
                            <option key={target} value={`#${target}`}>
                                #{target}
                            </option>
                        ))}
                    </select>

                    {/* SEQUENCE */}
                    <input
                        type="number"
                        placeholder="Sequence (lower = higher priority)"
                        value={formData.sequence}
                        onChange={(e) =>
                            setFormData((p) => ({ ...p, sequence: e.target.value }))
                        }
                        className="w-full border rounded px-3 py-2"
                    />

                    {/* ACTIVE */}
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            checked={formData.active}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, active: e.target.checked }))
                            }
                        />
                        Active
                    </label>

                    {/* PROGRESS */}
                    {uploading && (
                        <div className="flex items-center gap-2 text-sm text-blue-600">
                            <Loader2 className="animate-spin" size={16} />
                            Uploading... {progress}%
                        </div>
                    )}
                </div>

                {/* FOOTER */}
                <div className="flex justify-end gap-3 p-5 border-t">
                    <button onClick={onClose} className="border px-4 py-2 rounded">
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={uploading}
                        className="bg-blue-600 text-white px-4 py-2 rounded disabled:opacity-50"
                    >
                        {uploading ? "Processing..." : isEdit ? "Update" : "Create"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EditBannerModal;